import { hasCrashRoleData } from './merge-constants';

export const MERGE_FORM_ROLES = ['police', 'hospital', 'insurance'];

const MERGE_TYPE_TO_CONFIG_SECTIONS = {
  save_crash: ['crash', 'road'],
  save_unit: ['units'],
  save_person: ['people'],
  save_carried_person: ['people'],
};

const MERGE_TYPE_TO_STATE_PREFIXES = {
  save_crash: ['crash', 'road'],
  save_unit: ['unit'],
  save_person: ['person'],
  save_carried_person: ['person'],
};

/**
 * @param {unknown} value
 */
export function isEmptyMergeValue(value) {
  return value === null || value === undefined || value === '';
}

/**
 * @param {Record<string, unknown>} obj
 * @param {string[]} pathParts
 */
export function getNestedMergeValue(obj, pathParts = []) {
  return pathParts.reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

/**
 * @param {Record<string, unknown>} obj
 * @param {string[]} pathParts
 * @param {unknown} value
 */
function setNestedMergeValue(obj, pathParts, value) {
  if (!pathParts.length) return;
  let cursor = obj;
  for (let i = 0; i < pathParts.length - 1; i += 1) {
    const key = pathParts[i];
    if (!cursor[key] || typeof cursor[key] !== 'object') {
      cursor[key] = {};
    }
    cursor = cursor[key];
  }
  cursor[pathParts[pathParts.length - 1]] = value;
}

/**
 * @param {Record<string, unknown>} roleBranch
 * @param {string} path
 * @param {Set<string>} keysToIgnore
 */
function extractRoleBranchPaths(roleBranch, path = '', keysToIgnore = new Set()) {
  const paths = new Set();
  if (!roleBranch || typeof roleBranch !== 'object') return paths;

  Object.keys(roleBranch).forEach((key) => {
    if (keysToIgnore.has(key)) return;
    const newPath = path ? `${path}.${key}` : key;
    const value = roleBranch[key];
    if (value != null && typeof value === 'object' && !Array.isArray(value)) {
      extractRoleBranchPaths(value, newPath, keysToIgnore).forEach((p) => paths.add(p));
    } else {
      paths.add(newPath);
    }
  });

  return paths;
}

/**
 * @param {Record<string, { elements?: Array<{ label?: string }> }>} configuration
 * @param {string | undefined} mergeType
 * @param {Set<string>} keysToIgnore
 */
function configurationPathsForMergeType(configuration, mergeType, keysToIgnore = new Set()) {
  const paths = new Set();
  const configSections = MERGE_TYPE_TO_CONFIG_SECTIONS[mergeType] ?? [];
  const statePrefixes = MERGE_TYPE_TO_STATE_PREFIXES[mergeType] ?? [];

  configSections.forEach((sectionKey, index) => {
    const prefix = statePrefixes[index];
    if (!prefix) return;
    const elements = configuration?.[sectionKey]?.elements ?? [];
    elements.forEach((element) => {
      const label = element?.label;
      if (!label || keysToIgnore.has(label)) return;
      paths.add(`${prefix}.${label}`);
    });
  });

  return paths;
}

/**
 * Union of field paths from all active merge roles plus configuration catalog.
 *
 * @param {Record<string, unknown>} mergeState
 * @param {Record<string, { elements?: Array<{ label?: string }> }>} [configuration]
 * @param {string[]} [keysToIgnore]
 * @returns {string[]}
 */
export function prepareMergeFormInputPaths(
  mergeState,
  configuration,
  keysToIgnore = ['unitIndex', 'personIndex'],
) {
  const ignore = new Set(keysToIgnore);
  const paths = new Set();

  MERGE_FORM_ROLES.forEach((role) => {
    if (!hasCrashRoleData(mergeState?.[role])) return;
    extractRoleBranchPaths(mergeState[role], '', ignore).forEach((path) => paths.add(path));
  });

  configurationPathsForMergeType(configuration, mergeState?.type, ignore)
    .forEach((path) => paths.add(path));

  return [...paths].sort((a, b) => a.localeCompare(b));
}

/**
 * @param {Record<string, { permissions?: number[], Permissions?: number[] }>} configurationMap
 * @returns {Record<string, 'police' | 'hospital' | 'insurance'>}
 */
export function buildFieldOwnerRoleMap(configurationMap) {
  /** @type {Record<string, 'police' | 'hospital' | 'insurance'>} */
  const map = {};

  Object.entries(configurationMap ?? {}).forEach(([label, field]) => {
    const permissions = field?.permissions ?? field?.Permissions ?? [];
    if (permissions.length !== 1) return;

    const [basicOrg] = permissions;
    if (basicOrg === 1) map[label] = 'police';
    else if (basicOrg === 2) map[label] = 'hospital';
    else if (basicOrg === 3) map[label] = 'insurance';
  });

  return map;
}

/**
 * Default value for the merged column: owner-role wins, then first non-empty role.
 *
 * @param {string[]} pathParts
 * @param {Record<string, unknown>} mergeState
 * @param {Record<string, 'police' | 'hospital' | 'insurance'>} fieldOwnerRoleMap
 * @param {string} fieldKey
 */
export function resolveMergedFieldDefault(
  pathParts,
  mergeState,
  fieldOwnerRoleMap,
  fieldKey,
) {
  const ownerRole = fieldOwnerRoleMap[fieldKey];
  const roleOrder = ownerRole
    ? [ownerRole, ...MERGE_FORM_ROLES.filter((role) => role !== ownerRole)]
    : [...MERGE_FORM_ROLES];

  for (const role of roleOrder) {
    if (!hasCrashRoleData(mergeState?.[role])) continue;
    const value = getNestedMergeValue(mergeState[role], pathParts);
    if (!isEmptyMergeValue(value)) return value;
  }

  return undefined;
}

/**
 * Builds default merged section object (crash/road/unit/person) for react-hook-form.
 *
 * @param {Record<string, unknown>} mergeState
 * @param {Record<string, { permissions?: number[], Permissions?: number[] }>} configurationMap
 * @param {Record<string, { elements?: Array<{ label?: string }> }>} [configuration]
 */
export function buildMergedSectionDefaults(mergeState, configurationMap, configuration) {
  const ownerMap = buildFieldOwnerRoleMap(configurationMap);
  const paths = prepareMergeFormInputPaths(mergeState, configuration);
  const merged = {};

  paths.forEach((path) => {
    const parts = path.split('.');
    const fieldKey = parts[parts.length - 1];
    const value = resolveMergedFieldDefault(parts, mergeState, ownerMap, fieldKey);
    if (isEmptyMergeValue(value)) return;
    setNestedMergeValue(merged, parts, value);
  });

  return merged;
}
